import { FEATURE_OPTIONS } from '@/config/campaignConfigs'
import type { CampaignFeature } from '@/types/campaign.types'
import type { CompletedCampaignSummary } from '@/types/form.types'

export const COMPLETED_CAMPAIGNS_STORAGE_KEY = 'completedCampaigns'
const DEFAULT_CAMPAIGN_NAME = 'Campaign'

const FEATURE_ID_SET = new Set<CampaignFeature>(FEATURE_OPTIONS.map(option => option.id))

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null
)

const readString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

const readNullableString = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null
  }
  return readString(value)
}

const readFeatureId = (value: unknown): CampaignFeature | null => {
  const candidate = readString(value)
  if (!candidate) {
    return null
  }
  return FEATURE_ID_SET.has(candidate as CampaignFeature) ? candidate as CampaignFeature : null
}

const toCompletedCampaignSummary = (value: unknown): CompletedCampaignSummary | null => {
  if (!isRecord(value)) {
    return null
  }
  const submissionId = readString(value.submissionId)
  const createdAt = readString(value.createdAt)
  if (!submissionId || !createdAt) {
    return null
  }

  const campaignName = readString(value.campaignName) ?? DEFAULT_CAMPAIGN_NAME

  return {
    submissionId,
    campaignId: readNullableString(value.campaignId),
    createdAt,
    campaignName,
    featureId: readFeatureId(value.featureId),
    scheduleStart: readNullableString(value.scheduleStart),
    scheduleEnd: readNullableString(value.scheduleEnd),
  }
}

const isNotNull = <T>(value: T | null): value is T => value !== null

export const getCompletedCampaigns = (): CompletedCampaignSummary[] => {
  if (typeof window === 'undefined') {
    return []
  }
  try {
    const raw = window.localStorage.getItem(COMPLETED_CAMPAIGNS_STORAGE_KEY)
    if (!raw) {
      return []
    }
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed.map(toCompletedCampaignSummary).filter(isNotNull)
  } catch (error: unknown) {
    console.warn('Failed to load completed campaigns:', error)
    return []
  }
}

export const saveCompletedCampaign = (summary: CompletedCampaignSummary): void => {
  if (typeof window === 'undefined') {
    return
  }
  try {
    const existing = getCompletedCampaigns()
    const next = [
      summary,
      ...existing.filter(item => item.submissionId !== summary.submissionId),
    ]
    window.localStorage.setItem(COMPLETED_CAMPAIGNS_STORAGE_KEY, JSON.stringify(next))
  } catch (error: unknown) {
    console.warn('Failed to save completed campaign:', error)
  }
}

export const updateCompletedCampaignSchedule = (
  submissionId: string,
  scheduleStart: string | null,
  scheduleEnd: string | null
): void => {
  if (typeof window === 'undefined') {
    return
  }
  try {
    const existing = getCompletedCampaigns()
    const next = existing.map(item => (
      item.submissionId === submissionId
        ? {
            ...item,
            scheduleStart,
            scheduleEnd,
          }
        : item
    ))
    window.localStorage.setItem(COMPLETED_CAMPAIGNS_STORAGE_KEY, JSON.stringify(next))
  } catch (error: unknown) {
    console.warn('Failed to update campaign schedule:', error)
  }
}

export const updateCompletedCampaignCampaignId = (
  submissionId: string,
  campaignId: string
): void => {
  if (typeof window === 'undefined') {
    return
  }
  try {
    const existing = getCompletedCampaigns()
    const next = existing.map(item => (
      item.submissionId === submissionId
        ? {
            ...item,
            campaignId,
          }
        : item
    ))
    window.localStorage.setItem(COMPLETED_CAMPAIGNS_STORAGE_KEY, JSON.stringify(next))
  } catch (error: unknown) {
    console.warn('Failed to update campaign identifier:', error)
  }
}
